import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Container, Paper, TextField, Button,
  useTheme, alpha, Alert, Grid, InputAdornment, Divider
} from '@mui/material';
import {
  Email as EmailIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Send as SendIcon
} from '@mui/icons-material';
import axios from 'axios';

const ForgotPasswordPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/forgot-password`, {
        email: email.toLowerCase().trim()
      });

      if (response.data.ok) {
        setSuccess(true);
      } else {
        setError(response.data.error?.message || 'Failed to send reset email');
      }
    } catch (err: any) {
      const errorCode = err.response?.data?.error?.code;
      const errorMessage = err.response?.data?.error?.message;

      if (errorCode === 'USER_NOT_FOUND') {
        setError('This email is not registered. Please sign up first.');
      } else if (errorCode === 'ACCOUNT_PENDING') {
        setError('Your account is pending approval. Please wait for admin approval or contact support.');
      } else if (errorCode === 'ACCOUNT_REJECTED') {
        setError('Your account has been rejected. Please contact support for more information.');
      } else {
        setError(errorMessage || 'Failed to send reset email. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Grid container spacing={4}>
        {/* Left side - Forgot Password Form */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={3}
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              height: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {/* Form header */}
            <Box
              sx={{
                p: 3,
                background: `linear-gradient(90deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.dark})`,
                color: 'white'
              }}
            >
              <Typography variant="h4" fontWeight={600}>
                {!success ? 'Forgot Password?' : 'Check Your Email'}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9, mt: 0.5 }}>
                {!success
                  ? 'Reset your password in a few simple steps'
                  : 'Password reset link has been sent'
                }
              </Typography>
            </Box>

            {/* Form content */}
            <Box
              sx={{
                p: 4,
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1
              }}
            >
              {!success ? (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Enter your email address and we'll send you a link to reset your password.
                  </Typography>

                  {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                      {error}
                      {error.includes('not registered') && (
                        <>
                          {' '}
                          <RouterLink
                            to="/register"
                            style={{
                              color: 'inherit',
                              fontWeight: 600,
                              textDecoration: 'underline'
                            }}
                          >
                            Sign up here
                          </RouterLink>
                        </>
                      )}
                    </Alert>
                  )}

                  <Box component="form" onSubmit={handleSubmit}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoFocus
                      disabled={loading}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon color="action" />
                          </InputAdornment>
                        )
                      }}
                      sx={{ mb: 3 }}
                    />

                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      color="secondary"
                      size="large"
                      disabled={loading || !email}
                      startIcon={<SendIcon />}
                      sx={{
                        py: 1.5,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
                        mb: 2
                      }}
                    >
                      {loading ? 'Sending...' : 'Send Reset Link'}
                    </Button>

                    <Divider sx={{ my: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        OR
                      </Typography>
                    </Divider>

                    <Button
                      component={RouterLink}
                      to="/login"
                      fullWidth
                      startIcon={<ArrowBackIcon />}
                      variant="outlined"
                      color="primary"
                      sx={{
                        borderRadius: 2,
                        py: 1.5,
                        textTransform: 'none',
                        fontWeight: 500
                      }}
                    >
                      Back to Login
                    </Button>
                  </Box>
                </>
              ) : (
                <Box sx={{ textAlign: 'center' }}>
                  <CheckCircleIcon
                    sx={{
                      fontSize: 80,
                      color: 'success.main',
                      mb: 2,
                      animation: 'pulse 2s infinite'
                    }}
                  />
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Email Sent Successfully!
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    If an account exists for <strong>{email}</strong>, you will receive a password reset link shortly.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                    Didn't receive the email? Check your spam folder or try again.
                  </Typography>
                  <Button
                    component={RouterLink}
                    to="/login"
                    variant="contained"
                    color="secondary"
                    size="large"
                    fullWidth
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      fontWeight: 600,
                      textTransform: 'none',
                      boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)'
                    }}
                  >
                    Return to Login
                  </Button>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Right side - Information */}
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {/* Information message with gradient */}
            <Paper
              elevation={0}
              sx={{
                mb: 4,
                p: { xs: 3, md: 4 },
                borderRadius: 3,
                background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.1)})`,
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: '40%',
                  height: '100%',
                  opacity: 0.1,
                  background: `radial-gradient(circle, ${theme.palette.secondary.main} 0%, transparent 70%)`,
                  transform: 'translateX(30%)'
                }}
              />

              <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Typography
                  variant="h3"
                  component="h1"
                  gutterBottom
                  sx={{
                    fontWeight: 700,
                    background: `linear-gradient(90deg, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
                    backgroundClip: 'text',
                    textFillColor: 'transparent',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 1
                  }}
                >
                  Need Help?
                </Typography>

                <Typography variant="h6" color="text.secondary" paragraph>
                  We'll help you get back to your account
                </Typography>

                <Typography variant="body1" color="text.secondary" paragraph>
                  Password reset is quick and easy. Enter your email address and we'll send you instructions to create a new password.
                </Typography>
              </Box>
            </Paper>

            {/* Security tips */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                background: alpha(theme.palette.info.main, 0.05),
                border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
              }}
            >
              <Typography variant="h6" gutterBottom fontWeight={600} color="info.main">
                Security Tips
              </Typography>
              <Box component="ul" sx={{ pl: 2, mt: 2, mb: 0 }}>
                <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  The reset link will expire in 30 minutes
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Make sure to use a strong password with at least 8 characters
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Never share your password with anyone
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary">
                  If you didn't request this, you can safely ignore the email
                </Typography>
              </Box>
            </Paper>
          </Box>
        </Grid>
      </Grid>

      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Typography variant="body2" color="text.secondary">
          Don't have an account?{' '}
          <RouterLink
            to="/register"
            style={{
              color: theme.palette.primary.main,
              textDecoration: 'none',
              fontWeight: 600
            }}
          >
            Sign up
          </RouterLink>
        </Typography>
      </Box>
    </Container>
  );
};

export default ForgotPasswordPage;
